import React, { useCallback, useState, useMemo, useRef } from "react";
import styled, { css } from "styled-components";
import debounce from "lodash/debounce";
import isEqual from "lodash/isEqual";
import { useFormikContext } from "formik";
import Popover from "components/common/Popover";
import useClickOutside from "hooks/useClickOutside";
import { Input, ListGroupItem, ListGroup, Nav, NavItem } from "components/bootstrap";
import PaginatedList from "components/common/PaginatedList";
import TextOverflowEllipsis from "components/common/TextOverflowEllipsis";
import Spinner from "components/common/Spinner";
import Icon from "components/common/Icon";
import useIsKeyHeld from "hooks/useIsKeyHeld";
import useUserSearchFiltersQuery from "search-filter/hooks/useUserSearchFiltersQuery";
import generateId from "logic/generateId";
import NoSearchResult from "components/common/NoSearchResult";
import useSearchFiltersFormState from "search-filter/hooks/useSearchFiltersFormState";
import useSearchFiltersFormActions from "search-filter/hooks/useSearchFiltersFormActions";
import { FILTER_SCOPES } from "search-filter/constants";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
const DEFAULT_QUERY = {
  query: "",
  per_page: 10,
  page: 1,
  sort: "title",
  order: "asc",
  scope: "ALL"
};
const StyledListGroup = styled(ListGroup)`
  max-height: 200px;
  overflow-y: auto;
  width: 100%;
  overflow-x: hidden;
`;
const StyledListGroupItem = styled(ListGroupItem)`
  padding: 0;
`;
const Header = styled.div(
  ({ theme, $disabled }) => css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    background-color: ${$disabled ? theme.colors.variant.lightest.default : "transparent"};
    padding: 0 10px;
    height: 34px;
    cursor: ${$disabled ? "not-allowed" : "pointer"};
    user-select: none;
  `
);
const FilterContent = styled.div`
  padding: 5px 10px 0 10px;
`;
const FilterAttribute = styled.div`
  word-break: break-all;
  margin-bottom: 5px;
`;
const Hint = styled.div(
  ({ theme }) => css`
    margin-top: 9px;
    font-size: ${theme.fonts.size.small};
  `
);
const ToggleInfoIcon = styled(Icon)`
  cursor: pointer;
  padding: 3px;
`;
const StyledNav = styled(Nav)`
  margin-bottom: 6px;

  &.nav-justified > li {
    display: table-cell;
    width: 1%;
  }
`;
const SearchInput = styled(Input)`
  margin-bottom: 6px;
`;
const LoadPopoverItem = ({
  searchFilter,
  onSelect,
  disabled
}) => {
  const [open, setOpen] = useState(false);
  const toggleOpen = (e) => {
    e.stopPropagation();
    setOpen((cur) => !cur);
  };
  const handleClick = () => onSelect({ ...searchFilter, frontendId: generateId(), type: "referenced" });
  return /* @__PURE__ */ React.createElement(StyledListGroupItem, null, /* @__PURE__ */ React.createElement(Header, { onClick: disabled ? void 0 : handleClick, $disabled: disabled }, /* @__PURE__ */ React.createElement(TextOverflowEllipsis, null, searchFilter.title || searchFilter.queryString), /* @__PURE__ */ React.createElement(
    ToggleInfoIcon,
    {
      name: open ? "keyboard_arrow_up" : "keyboard_arrow_down",
      "data-testid": `open-filter-details-button-${searchFilter.id}`,
      onClick: toggleOpen
    }
  )), open && /* @__PURE__ */ React.createElement(FilterContent, null, /* @__PURE__ */ React.createElement(FilterAttribute, null, /* @__PURE__ */ React.createElement("i", null, "Query:"), " ", searchFilter.queryString), searchFilter.title && /* @__PURE__ */ React.createElement(FilterAttribute, null, /* @__PURE__ */ React.createElement("i", null, "Title:"), " ", searchFilter.title), searchFilter.description && /* @__PURE__ */ React.createElement(FilterAttribute, null, /* @__PURE__ */ React.createElement("i", null, "Description:"), " ", searchFilter.description)));
};
const SearchFiltersOverviewPopover = ({ children = void 0, onClose, show }) => {
  const isShiftHeld = useIsKeyHeld("Shift");
  const { submitForm } = useFormikContext();
  const [filterQuery, setSearchFilter] = useState(DEFAULT_QUERY);
  const {
    data: { filters, pagination },
    isFetching
  } = useUserSearchFiltersQuery(filterQuery);
  const selectedFilters = useSearchFiltersFormState();
  const initiallySelectedFilters = useRef(selectedFilters);
  const { addFilter } = useSearchFiltersFormActions();
  const sendTelemetry = useSendTelemetry();
  const [popoverRef, setPopoverRef] = useState(null);
  const [control, setControl] = useState(null);
  useClickOutside(onClose, null, [popoverRef, control]);
  const selectedReferencedFiltersIds = useMemo(
    () => selectedFilters.toList().reduce((res, cur) => {
      if (cur.type === "referenced") return res.add(cur.id);
      return res;
    }, /* @__PURE__ */ new Set([])),
    [selectedFilters]
  );
  const handleSearchChange = useCallback(
    (newSearchQuery) => {
      setSearchFilter((cur) => ({ ...cur, page: DEFAULT_QUERY.page, query: newSearchQuery }));
    },
    [setSearchFilter]
  );
  const handlePaginationChange = useCallback(
    (page) => {
      setSearchFilter((cur) => ({ ...cur, page }));
    },
    [setSearchFilter]
  );
  const debounceOnSearch = debounce((value) => handleSearchChange(value), 1e3);
  const onSelectItem = useCallback(
    (filter) => {
      addFilter(filter, !isShiftHeld);
      if (!isShiftHeld) {
        onClose();
      }
      sendTelemetry(TELEMETRY_EVENT_TYPE.SEARCH_FILTER_LOADED, {
        app_pathname: "search",
        app_section: "search-filter"
      });
    },
    [addFilter, isShiftHeld, onClose, sendTelemetry]
  );
  const onOverlayClose = useCallback(() => {
    if (!isEqual(selectedFilters, initiallySelectedFilters.current) && show) {
      submitForm();
    }
  }, [selectedFilters, show, submitForm]);
  const onTabSelect = useCallback(
    (tab) => {
      setSearchFilter((cur) => ({ ...cur, page: 1, scope: tab }));
    },
    [setSearchFilter]
  );
  return /* @__PURE__ */ React.createElement(
    Popover,
    {
      position: "bottom-start",
      withArrow: true,
      width: 300,
      opened: show,
      onClose: onOverlayClose,
      closeOnClickOutside: false
    },
    /* @__PURE__ */ React.createElement(Popover.Target, null, /* @__PURE__ */ React.createElement("div", { ref: setControl }, children)),
    /* @__PURE__ */ React.createElement(Popover.Dropdown, { title: "Search Filters" }, /* @__PURE__ */ React.createElement("div", { ref: setPopoverRef }, /* @__PURE__ */ React.createElement(
      SearchInput,
      {
        type: "text",
        id: "search-filters-input",
        formGroupClassName: "",
        placeholder: "Search for filters",
        onChange: ({ target: { value } }) => debounceOnSearch(value)
      }
    ), /* @__PURE__ */ React.createElement(StyledNav, { bsStyle: "pills", justified: true, activeKey: filterQuery.scope, onSelect: onTabSelect, bsSize: "sm" }, /* @__PURE__ */ React.createElement(NavItem, { eventKey: FILTER_SCOPES.ALL }, "All"), /* @__PURE__ */ React.createElement(NavItem, { eventKey: FILTER_SCOPES.MY_FILTERS }, "My Filters")), !(filters == null ? void 0 : filters.length) && isFetching && /* @__PURE__ */ React.createElement(Spinner, null), !!(filters == null ? void 0 : filters.length) && /* @__PURE__ */ React.createElement(
      PaginatedList,
      {
        showPageSizeSelect: false,
        totalItems: pagination.total,
        hidePreviousAndNextPageLinks: true,
        hideFirstAndLastPageLinks: true,
        activePage: filterQuery.page,
        pageSize: filterQuery.per_page,
        onChange: handlePaginationChange,
        useQueryParameter: false
      },
      /* @__PURE__ */ React.createElement(StyledListGroup, null, filters.map((filter) => /* @__PURE__ */ React.createElement(
        LoadPopoverItem,
        {
          disabled: selectedReferencedFiltersIds.has(filter.id),
          searchFilter: filter,
          onSelect: onSelectItem,
          key: filter.id
        }
      )))
    ), !isFetching && !(filters == null ? void 0 : filters.length) && /* @__PURE__ */ React.createElement(NoSearchResult, null, "No filters have been found."), /* @__PURE__ */ React.createElement(Hint, null, /* @__PURE__ */ React.createElement("i", null, "Hold Shift to select multiple"))))
  );
};
export default SearchFiltersOverviewPopover;
